import { getCurrentUser, onUserChange } from "../auth/session";
import { t } from "../i18n";

const SKIP_CLASS = "skip-onboarding";
const LEAVE_MS = 850;

let leaveTimer: ReturnType<typeof setTimeout> | null = null;
let leaveResolver: (() => void) | null = null;

function setBackgroundInert(inert: boolean): void {
  for (const id of ["viewport", "app-chrome", "app-main"]) {
    document.getElementById(id)?.toggleAttribute("inert", inert);
  }
}

function landingEl(): HTMLElement | null {
  return document.getElementById("onboarding-landing");
}

export function hasOnboardingBypass(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname || "";
  const q = window.location.search || "";
  if (/^\/memorial\//.test(path)) return true;
  return /(?:^|[?&])(?:reset_token|verify_token|session_id|headstone_invite)=/.test(
    q,
  );
}

function isLandingGone(el: HTMLElement): boolean {
  return el.classList.contains("is-gone");
}

export function isOnboardingVisible(): boolean {
  const el = landingEl();
  if (!el || isLandingGone(el)) return false;
  if (document.documentElement.classList.contains(SKIP_CLASS)) return false;
  return true;
}

function finishLeave(el: HTMLElement): void {
  el.classList.add("is-gone");
  el.classList.remove("is-leaving");
  setBackgroundInert(false);
  if (leaveTimer) {
    clearTimeout(leaveTimer);
    leaveTimer = null;
  }
  const resolve = leaveResolver;
  leaveResolver = null;
  resolve?.();
}

export function dismissOnboarding(
  opts: { animate?: boolean } = {},
): Promise<void> {
  const el = landingEl();
  if (!el || isLandingGone(el)) return Promise.resolve();

  const skipPaint = document.documentElement.classList.contains(SKIP_CLASS);
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    .matches;
  if (!opts.animate || skipPaint || reduceMotion) {
    el.classList.add("is-gone");
    el.classList.remove("is-leaving");
    setBackgroundInert(false);
    return Promise.resolve();
  }

  if (el.classList.contains("is-leaving")) {
    return new Promise((resolve) => {
      const prev = leaveResolver;
      leaveResolver = () => {
        prev?.();
        resolve();
      };
    });
  }

  return new Promise((resolve) => {
    leaveResolver = resolve;
    el.classList.add("is-leaving");
    const onEnd = (event: TransitionEvent) => {
      if (event.target !== el || event.propertyName !== "transform") return;
      el.removeEventListener("transitionend", onEnd);
      finishLeave(el);
    };
    el.addEventListener("transitionend", onEnd);
    leaveTimer = setTimeout(() => {
      el.removeEventListener("transitionend", onEnd);
      finishLeave(el);
    }, LEAVE_MS);
  });
}

export function showOnboarding(): void {
  if (getCurrentUser() || hasOnboardingBypass()) return;
  document.documentElement.classList.remove(SKIP_CLASS);
  const el = landingEl();
  if (!el) return;
  if (leaveTimer) {
    clearTimeout(leaveTimer);
    leaveTimer = null;
  }
  leaveResolver?.();
  leaveResolver = null;
  el.classList.remove("is-gone", "is-leaving");
  setBackgroundInert(true);
}

export function updateOnboardingCopy(): void {
  const setText = (id: string, key: string) => {
    const node = document.getElementById(id);
    if (node) node.textContent = t(key);
  };
  setText("onboarding-eyebrow", "onboarding.eyebrow");
  setText("onboarding-title", "onboarding.title");
  setText("onboarding-lead", "onboarding.lead");
  setText("onboarding-body", "onboarding.body");
  setText("onboarding-create", "onboarding.create");
  setText("onboarding-explore", "onboarding.explore");
  setText("onboarding-login", "onboarding.login");
  setText("onboarding-bridge-title", "onboarding.bridgeTitle");
}

export async function syncOnboardingToSession(): Promise<void> {
  if (getCurrentUser() || hasOnboardingBypass()) {
    document.documentElement.classList.add(SKIP_CLASS);
    await dismissOnboarding({ animate: false });
    return;
  }
  document.documentElement.classList.remove(SKIP_CLASS);
  const el = landingEl();
  if (el?.classList.contains("is-gone")) showOnboarding();
}

export function initOnboarding(): void {
  updateOnboardingCopy();
  setBackgroundInert(isOnboardingVisible());

  document.getElementById("onboarding-create")?.addEventListener("click", () => {
    void (async () => {
      const { openCreateMemorial } = await import("./purchase");
      openCreateMemorial();
      await dismissOnboarding({ animate: true });
    })();
  });

  document.getElementById("onboarding-explore")?.addEventListener("click", () => {
    void dismissOnboarding({ animate: true });
  });

  document.getElementById("onboarding-login")?.addEventListener("click", () => {
    window.openAuthModal?.("login");
  });

  onUserChange((user) => {
    if (!user) return;
    void dismissOnboarding({ animate: true }).then(() => {
      if (getCurrentUser()) document.documentElement.classList.add(SKIP_CLASS);
    });
  });
}
